import { ActionIcon, Button, ButtonGroup, Popover, Stack } from "@mantine/core";
import { IconFilter, IconFilterFilled } from "@tabler/icons-react";
import type { MRT_ColumnDef, MRT_FilterFn } from "mantine-react-table-open";
import { useMemo } from "react";
import { TodoStatus } from "../../../../utils/backend-types";
import { StatusSymbol, StatusSymbolColor } from "../../../Icons/StatusSymbol";
import { getAllPossibleOnboardingTodoLabels } from "../../../Todos/utils/getAllPossibleOnboardingTodoLabels";
import type {
  OnboardingTodo,
  SiteOverviewSite,
  SiteStatusSite
} from "../../site-status-types";
import { calculateNumDoneOnboardingTodos } from "../utils/calculateNumDoneOnboardingTodos";

interface OnboardingStatusColumnProps {
  onClickOnboardingTodosCell: (todos: Array<OnboardingTodo>) => void;
}

function getOnboardingStatusColumn({
  onClickOnboardingTodosCell
}: OnboardingStatusColumnProps): MRT_ColumnDef<
  SiteOverviewSite | SiteStatusSite
> {
  return {
    header: "Status Onboarding-Aufgaben",
    accessorKey: "onboardingTodos",
    Cell: ({ row }) => (
      <OnboardingTodosCell
        todos={row.original.onboardingTodos}
        onClick={onClickOnboardingTodosCell}
      />
    ),
    enableGlobalFilter: false,
    sortingFn: (rowA, rowB) =>
      sortOnboardingTodos(
        rowA.getValue<Array<OnboardingTodo>>("onboardingTodos"),
        rowB.getValue<Array<OnboardingTodo>>("onboardingTodos")
      ),
    filterTooltipValueFn: (filterValue) => {
      if (!filterValue || Object.keys(filterValue).length === 0) {
        return "Keine Filter gesetzt";
      }
      const parts: string[] = [];
      Object.entries(filterValue).forEach(([label, isDone]) => {
        parts.push(`${label}: ${isDone ? "Erledigt" : "Offen"}`);
      });
      return parts.join(", ");
    },
    Filter: ({ column, table }) => {
      const filterValue = column.getFilterValue() as LabelFilter | undefined;
      const hasFilterValue =
        filterValue !== undefined && Object.keys(filterValue).length > 0;

      return (
        <Popover>
          <Popover.Target>
            <ActionIcon>
              {hasFilterValue ? (
                <IconFilterFilled size="1.3rem" />
              ) : (
                <IconFilter size="1.3rem" />
              )}
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <OnboardingTodoFilter column={column} table={table} />
          </Popover.Dropdown>
        </Popover>
      );
    },
    filterFn: filterOnboardingTodoState
  };
}

const NO_TODOS_TEXT = "Keine Aufgaben";

interface OnboardingTodosCellProps {
  todos: Array<OnboardingTodo>;
  onClick: (todos: Array<OnboardingTodo>) => void;
}

function OnboardingTodosCell({ todos, onClick }: OnboardingTodosCellProps) {
  const numTodos = todos.length;
  const textAndSymbolStyle = { display: "flex", alignItems: "center" };

  if (numTodos === 0) {
    return (
      <div style={textAndSymbolStyle}>
        <StatusSymbol noStatus />
        <span>{NO_TODOS_TEXT}</span>
      </div>
    );
  }

  const numDone = calculateNumDoneOnboardingTodos(todos);

  let statusSymbolColor = StatusSymbolColor.Red;

  if (numTodos === numDone) {
    statusSymbolColor = StatusSymbolColor.Green;
  } else if (numDone > 0) {
    statusSymbolColor = StatusSymbolColor.Warning;
  }

  return (
    <div
      style={{ ...textAndSymbolStyle, cursor: "pointer" }}
      onClick={() => onClick(todos)}
    >
      <StatusSymbol color={statusSymbolColor} />
      <span>
        {numDone}/{numTodos} erledigt
      </span>
    </div>
  );
}

interface LabelFilter {
  [key: string]: boolean | undefined;
}

function setFilter(
  todoLabel: string,
  value: boolean | null,
  prevColumnFilter: LabelFilter,
  setColumnFilter: (newLabelFilter: LabelFilter) => void
) {
  if (prevColumnFilter && Object.keys(prevColumnFilter).includes(todoLabel)) {
    if (value === null) {
      const { [todoLabel]: _, ...rest } = prevColumnFilter;
      setColumnFilter(rest);
    } else {
      setColumnFilter({ ...prevColumnFilter, [todoLabel]: value });
    }
  } else if (value !== null) {
    setColumnFilter({ ...prevColumnFilter, [todoLabel]: value });
  }
}

const filterOnboardingTodoState: MRT_FilterFn<
  SiteOverviewSite | SiteStatusSite
> = (row, _columnId, filterValue: LabelFilter) => {
  if (!filterValue || Object.keys(filterValue).length === 0) {
    return true;
  }

  const todos = row.original.onboardingTodos;
  const included = Object.keys(filterValue).reduce(
    (resultIncluded, labelInFilter) => {
      const stateOfTodo: TodoStatus | undefined = todos.find(
        (todo) => todo.label === labelInFilter
      )?.status;
      const includedForThisLabel: boolean =
        stateOfTodo ===
        (filterValue[labelInFilter] ? TodoStatus.Done : TodoStatus.Open);
      return resultIncluded && includedForThisLabel;
    },
    true
  );

  return included;
};

function OnboardingTodoFilter({ column, table }) {
  const allPossibleOnboardingTodoLabels = useMemo(
    () => getAllPossibleOnboardingTodoLabels(),
    []
  );
  const setOnboardingTodoFilter = (newLabelFilter) =>
    table.setColumnFilters((prev) => [
      ...prev.filter((filter) => filter.id !== column.id),
      { id: column.id, value: newLabelFilter }
    ]);

  const filterValue = column.getFilterValue() as LabelFilter | undefined;

  return (
    <Stack h={400} style={{ overflowY: "auto" }}>
      {allPossibleOnboardingTodoLabels.map((label) => {
        return (
          <Stack gap={0} key={label}>
            {label}:
            <ButtonGroup>
              <Button
                color="lightgray"
                variant={
                  filterValue && filterValue[label] === true
                    ? "filled"
                    : "outline"
                }
                onClick={() =>
                  setFilter(
                    label,
                    true,
                    column.getFilterValue() as LabelFilter,
                    setOnboardingTodoFilter
                  )
                }
              >
                <StatusSymbol color={StatusSymbolColor.Green} />
              </Button>
              <Button
                color="lightgray"
                variant={
                  filterValue && filterValue[label] === false
                    ? "filled"
                    : "outline"
                }
                onClick={() =>
                  setFilter(
                    label,
                    false,
                    column.getFilterValue() as LabelFilter,
                    setOnboardingTodoFilter
                  )
                }
              >
                <StatusSymbol color={StatusSymbolColor.Red} />
              </Button>
              <Button
                color="lightgray"
                variant={
                  !filterValue || filterValue[label] === undefined
                    ? "filled"
                    : "outline"
                }
                onClick={() =>
                  setFilter(
                    label,
                    null,
                    column.getFilterValue() as LabelFilter,
                    setOnboardingTodoFilter
                  )
                }
              >
                Alle
              </Button>
            </ButtonGroup>
          </Stack>
        );
      })}
    </Stack>
  );
}

function sortOnboardingTodos(
  a: Array<OnboardingTodo>,
  b: Array<OnboardingTodo>
) {
  const aTotalDone = calculateNumDoneOnboardingTodos(a);
  const aTotalTodos = a.length;
  const aPercent = aTotalTodos > 0 ? aTotalDone / aTotalTodos : -1;
  const bTotalDone = calculateNumDoneOnboardingTodos(b);
  const bTotalTodos = b.length;
  const bPercent = bTotalTodos > 0 ? bTotalDone / bTotalTodos : -1;

  if (aPercent < bPercent) {
    return -1;
  } else if (aPercent > bPercent) {
    return 1;
  }

  return 0;
}

export { getOnboardingStatusColumn };
